'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, type PropsWithChildren, use, useEffect, useState } from 'react';
import { type DbSession, getDbSession, wipeDbSessions } from './db-session.ts';
type Context = {
  hasProvider: boolean;
  isOpening: boolean;
  session: DbSession | undefined;
  userId: null | number;
};
const DbContext = createContext<Context>({
  hasProvider: false,
  isOpening: false,
  session: undefined,
  userId: null
});
type Props = PropsWithChildren<{
  userId: null | number;
}>;
export const DbSessionProvider = t0 => {
  const $ = _c(10);
  const {
    children,
    userId
  } = t0;
  const [sessionState, setSessionState] = useState();
  if (userId === null && sessionState !== undefined) {
    setSessionState(undefined);
  }
  let t1;
  let t2;
  if ($[0] !== userId) {
    t1 = () => {
      if (userId === null) {
        wipeDbSessions();
        return;
      }
      let isCurrent = true;
      getDbSession(userId).then(opened => {
        if (isCurrent) {
          setSessionState({
            session: opened,
            userId
          });
        }
      });
      return () => {
        isCurrent = false;
      };
    };
    t2 = [userId];
    $[0] = userId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const session = sessionState?.userId === userId ? sessionState.session : undefined;
  const isOpening = userId !== null && session === undefined;
  let t3;
  if ($[3] !== isOpening || $[4] !== session || $[5] !== userId) {
    t3 = {
      hasProvider: true,
      isOpening,
      session,
      userId
    };
    $[3] = isOpening;
    $[4] = session;
    $[5] = userId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <DbContext value={t3} data-sentry-element="DbContext" data-sentry-component="DbSessionProvider" data-sentry-source-file="db-session-provider.tsx">{children}</DbContext>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};

/**
 * The open database, or undefined on the server, during hydration and while it opens. For data
 * that may render empty first (select options and other reference data).
 */
export const useOpenDbSession = () => use(DbContext).session;

/** Whether an authenticated provider is still opening its per-user session. */
export const useDbSessionState = () => use(DbContext);

/**
 * Suspends until the user's database is open. Collections are browser-only, so components
 * calling this must not render on the server (load them with `next/dynamic` and `ssr: false`).
 */
export const useDbSession = () => {
  const {
    userId
  } = use(DbContext);
  if (userId === null) {
    throw new Error('useDbSession must be used within a DbSessionProvider with an authenticated user');
  }
  return use(getDbSession(userId));
};